/**
 * Valori originali del merchandising plan e del budget (come caricati dall'Excel).
 * Il backend (optim-get-mp-budget-originals) manda solo i tipi filato e le sotto-consegne
 * che differiscono dal salvato: qui si indicizzano per negozio e si confrontano con lo
 * stato locale, che puo' avere modifiche non ancora salvate.
 */

export type TfMetric = 'economicValue' | 'items' | 'uniqueModels' | 'skuCount';
export type BudgetMetric = 'value' | 'items';

type TfOriginalPayload = {
  items_total?: number | null;
  currency_value?: number | null;
  models_unique?: number | null;
  avg_price?: number | null;
  sku_count?: number | null;
};

export type MpBudgetOriginalsResponse = {
  mp_tracked?: boolean;
  budget_tracked?: boolean;
  mp?: Array<{ sub_idx: number; cod_negozio?: string | null; tipo_filato?: Record<string, TfOriginalPayload> }>;
  budget?: Array<{ sub_idx: number; cod_negozio?: string | null; consegne?: Array<{ sotto_consegna: string; value?: number | null; items?: number | null }> }>;
};

export type MpOriginalsIndex = {
  mpTracked: boolean;
  budgetTracked: boolean;
  // negozio -> tipo filato -> metrica -> valore originale
  mp: Record<string, Record<string, Partial<Record<TfMetric, number | null>>>>;
  // negozio -> sotto-consegna -> metrica -> valore originale
  budget: Record<string, Record<string, Partial<Record<BudgetMetric, number | null>>>>;
};

export const EMPTY_MP_ORIGINALS: MpOriginalsIndex = { mpTracked: false, budgetTracked: false, mp: {}, budget: {} };

const TF_FIELD: Record<TfMetric, keyof TfOriginalPayload> = {
  economicValue: 'currency_value',
  items: 'items_total',
  uniqueModels: 'models_unique',
  skuCount: 'sku_count',
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export function indexMpOriginals(response: unknown): MpOriginalsIndex {
  const r = (response ?? {}) as MpBudgetOriginalsResponse;
  const index: MpOriginalsIndex = { mpTracked: !!r.mp_tracked, budgetTracked: !!r.budget_tracked, mp: {}, budget: {} };
  for (const line of r.mp ?? []) {
    const shop = String(line.cod_negozio ?? '');
    for (const [tipoFilato, values] of Object.entries(line.tipo_filato ?? {})) {
      const metrics: Partial<Record<TfMetric, number | null>> = {};
      (Object.keys(TF_FIELD) as TfMetric[]).forEach((metric) => {
        metrics[metric] = toNumberOrNull(values?.[TF_FIELD[metric]]);
      });
      (index.mp[shop] ??= {})[tipoFilato] = metrics;
    }
  }
  for (const line of r.budget ?? []) {
    const shop = String(line.cod_negozio ?? '');
    for (const entry of line.consegne ?? []) {
      (index.budget[shop] ??= {})[entry.sotto_consegna] = {
        value: toNumberOrNull(entry.value),
        items: toNumberOrNull(entry.items ?? 0),
      };
    }
  }
  return index;
}

export type SavedValues = Pick<MpOriginalsIndex, 'mp' | 'budget'>;

/**
 * Il backend manda solo cio' che il salvato ha di diverso dall'Excel: per un MP tracciato,
 * tutto il resto ha come originale proprio il valore salvato. Con i valori salvati letti
 * al caricamento si completa l'indice, cosi' l'originale compare appena si modifica un
 * vincolo, anche prima di salvare (per esempio applicando una proposta dello smistatore).
 */
export function withSavedFallback(index: MpOriginalsIndex, saved: SavedValues): MpOriginalsIndex {
  const fill = <M extends string>(
    tracked: boolean,
    diff: Record<string, Record<string, Partial<Record<M, number | null>>>>,
    base: Record<string, Record<string, Partial<Record<M, number | null>>>>,
  ) => {
    if (!tracked) return diff;
    const out: Record<string, Record<string, Partial<Record<M, number | null>>>> = {};
    for (const [shop, entries] of Object.entries(base)) {
      out[shop] = {};
      for (const [key, metrics] of Object.entries(entries)) {
        out[shop][key] = { ...metrics, ...(diff[shop]?.[key] ?? {}) };
      }
    }
    for (const [shop, entries] of Object.entries(diff)) {
      out[shop] = { ...(out[shop] ?? {}) };
      for (const [key, metrics] of Object.entries(entries)) {
        out[shop][key] = { ...(out[shop][key] ?? {}), ...metrics };
      }
    }
    return out;
  };
  return {
    ...index,
    mp: fill(index.mpTracked, index.mp, saved.mp),
    budget: fill(index.budgetTracked, index.budget, saved.budget),
  };
}

/** Valore originale da mostrare, solo se c'e' ed e' diverso da quello attuale. */
export function changedOriginal(original: number | null | undefined, current: number | null | undefined): number | null | undefined {
  if (original === undefined) return undefined;
  const a = toNumberOrNull(original);
  const b = toNumberOrNull(current);
  if (a === null && b === null) return undefined;
  if (a !== null && b !== null && Math.abs(a - b) < 1e-6) return undefined;
  return a;
}

/**
 * Valore da passare all'editor per tornare all'originale. Con una run collegata a schermo
 * c'e' la capacita' residua, mentre il salvato (e l'originale) e' il grezzo: si sposta il
 * residuo della stessa differenza, come fa l'editor.
 */
export function restoreUpperBound(original: number, metric: { upperBound?: number | null; rawUpperBound?: number | null }): number {
  if (metric.rawUpperBound === undefined || metric.rawUpperBound === null) return original;
  return Number(metric.upperBound ?? 0) + (original - Number(metric.rawUpperBound));
}

export function formatOriginal(value: number | null): string {
  if (value === null) return 'NA';
  return value.toLocaleString('it-IT', { maximumFractionDigits: 2 });
}
